/**
 * Submit a feedback note, then (best-effort) upload its screenshot.
 *
 * The note POST goes through axios so the AuthContext interceptor attaches the
 * Bearer token. The screenshot PUT uses plain `fetch` on purpose: the presigned
 * URL is already signed, and sending an Authorization header would break the
 * signature. The note is the source of truth — a failed image upload is logged
 * but does not fail the submit (the row already exists, referencing the key).
 */

import axios from "axios";

import { getApiBaseUrl } from "@/config/api";
import { logger } from "@/utils/logger";

import type { FeedbackCategory } from "./feedbackCapture";

export interface SubmitFeedbackArgs {
  category: FeedbackCategory;
  text: string;
  context: Record<string, unknown>;
  screenshot: Blob | null;
}

export interface SubmitFeedbackResult {
  fbId: string;
  screenshotUploaded: boolean;
}

export async function submitFeedback(args: SubmitFeedbackArgs): Promise<SubmitFeedbackResult> {
  const { data } = await axios.post(`${getApiBaseUrl()}/api/feedback/note`, {
    category: args.category,
    text: args.text,
    context: args.context,
    has_screenshot: args.screenshot !== null,
  });

  const fbId: string = data.fb_id;
  const uploadUrl: string | null = data.screenshot_upload_url ?? null;

  let screenshotUploaded = false;
  if (args.screenshot && uploadUrl) {
    try {
      const res = await fetch(uploadUrl, {
        method: "PUT",
        body: args.screenshot,
        headers: { "Content-Type": "image/jpeg" },
      });
      screenshotUploaded = res.ok;
      if (!res.ok) {
        logger.warn(`[feedback] screenshot upload failed for ${fbId}: ${res.status}`);
      }
    } catch (err) {
      // A CORS- or network-blocked PUT rejects the fetch outright. The note row
      // already exists at this point, so report the dropped image instead of
      // failing the submit — a retry would file a duplicate note.
      logger.warn(`[feedback] screenshot upload threw for ${fbId}: ${String(err)}`);
    }
  }

  return { fbId, screenshotUploaded };
}
